import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
// @ts-ignore
import serviceTypeRequest from '@/services/admin/service-type-service';
const CACHE_DURATION = 10 * 60 * 1000;
export const useServiceTypesStore = defineStore('serviceTypes', () => {
    const serviceTypes = ref([]);
    const isLoading = ref(false);
    const error = ref(null);
    const lastFetched = ref(null);
    const getById = computed(() => {
        return (id) => serviceTypes.value.find((st) => st.id === id);
    });
    const getBySlug = computed(() => {
        return (slug) => serviceTypes.value.find((st) => st.slug === slug);
    });
    const isCacheValid = computed(() => {
        if (!lastFetched.value)
            return false;
        return Date.now() - lastFetched.value.getTime() < CACHE_DURATION;
    });
    const asSelectOptions = computed(() => {
        return serviceTypes.value.map((st) => ({
            value: st.id,
            label: st.name,
        }));
    });
    const totalServiceTypes = computed(() => serviceTypes.value.length);
    async function fetchAll(forceRefresh = false) {
        if (!forceRefresh && isCacheValid.value) {
            return serviceTypes.value;
        }
        isLoading.value = true;
        error.value = null;
        try {
            const response = await serviceTypeRequest.getAll();
            if (response.status === 200) {
                serviceTypes.value = response.data;
                lastFetched.value = new Date();
            }
            return serviceTypes.value;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : 'Error fetching service types';
            console.error('Error loading service types:', err);
            return [];
        }
        finally {
            isLoading.value = false;
        }
    }
    async function fetchById(id) {
        const cached = serviceTypes.value.find((st) => st.id === id);
        if (cached)
            return cached;
        isLoading.value = true;
        error.value = null;
        try {
            const response = await serviceTypeRequest.getById(id);
            if (response.status === 200) {
                const serviceType = response.data;
                const index = serviceTypes.value.findIndex((st) => st.id === id);
                if (index !== -1) {
                    serviceTypes.value[index] = serviceType;
                }
                else {
                    serviceTypes.value.push(serviceType);
                }
                return serviceType;
            }
            return null;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : 'Error fetching service type';
            console.error('Error loading service type:', err);
            return null;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function fetchBySlug(slug) {
        const cached = serviceTypes.value.find((st) => st.slug === slug);
        if (cached)
            return cached;
        isLoading.value = true;
        error.value = null;
        try {
            const response = await serviceTypeRequest.getBySlug(slug);
            if (response.status === 200) {
                const serviceType = response.data;
                const index = serviceTypes.value.findIndex((st) => st.slug === slug);
                if (index !== -1) {
                    serviceTypes.value[index] = serviceType;
                }
                else {
                    serviceTypes.value.push(serviceType);
                }
                return serviceType;
            }
            return null;
        }
        catch (err) {
            error.value = err instanceof Error ? err.message : 'Error fetching service type';
            console.error('Error loading service type by slug:', err);
            return null;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function getSchema(id) {
        try {
            const response = await serviceTypeRequest.getSchema(id);
            if (response.status === 200) {
                return response.data;
            }
            return null;
        }
        catch (err) {
            console.error('Error loading service type schema:', err);
            return null;
        }
    }
    function invalidateCache() {
        lastFetched.value = null;
    }
    function $reset() {
        serviceTypes.value = [];
        isLoading.value = false;
        error.value = null;
        lastFetched.value = null;
    }
    return {
        serviceTypes,
        isLoading,
        error,
        lastFetched,
        getById,
        getBySlug,
        isCacheValid,
        asSelectOptions,
        totalServiceTypes,
        fetchAll,
        fetchById,
        fetchBySlug,
        getSchema,
        invalidateCache,
        $reset,
    };
});
